import { Link } from "react-router-dom";
import { CheckCircle, ArrowRight, Camera, Ruler, Target, HeartHalf, User } from "@phosphor-icons/react";

/**
 * OnboardingProgressRing — visible completion streak for the 5 setup habits
 * that make the rest of the app work properly.
 *
 * Steps (in the order they nudge the client through):
 *   1. Profile complete (height / sex / occupation)
 *   2. Starting photos uploaded (at least 1)
 *   3. Goals set
 *   4. First wellness check-in logged
 *   5. First body stats logged
 *
 * Renders only until all 5 are done — then hides itself so the Today page
 * isn't cluttered forever. Once complete, a small celebratory "5/5 dialled"
 * card renders once, dismissable via localStorage.
 *
 * Data source: `/client/onboarding/status` (already fetched by ClientToday).
 */
const DISMISS_KEY = "onboarding-ring-completed-dismissed";

const STEPS = [
  {
    key: "profile_complete",
    label: "Profile",
    hint: "Height, sex, occupation",
    to: "/app/stats?tab=profile",
    icon: User,
  },
  {
    key: "photos_uploaded",
    label: "Starting photos",
    hint: "Upload 1–3 front-on shots",
    to: "/app/stats?tab=profile",
    icon: Camera,
  },
  {
    key: "has_goals",
    label: "Goals",
    hint: "Weight-loss target + why",
    to: "/app/stats?tab=profile",
    icon: Target,
  },
  {
    key: "has_wellness",
    label: "First wellness check",
    hint: "Sleep, energy, stress, mood",
    to: "/app/stats?tab=wellness",
    icon: HeartHalf,
  },
  {
    key: "has_body_stats",
    label: "First body stats",
    hint: "Weight, waist, chest",
    to: "/app/stats?tab=body",
    icon: Ruler,
  },
];

export default function OnboardingProgressRing({ status }) {
  if (!status) return null;

  // Normalize the 5 booleans from /client/onboarding/status
  const flags = {
    profile_complete: !!status.profile_complete,
    photos_uploaded: (status.photos_count || 0) >= 1,
    has_goals: !!status.has_goals,
    has_wellness: !!status.has_wellness,
    has_body_stats: !!status.has_body_stats,
  };
  const doneCount = STEPS.filter((s) => flags[s.key]).length;
  const total = STEPS.length;
  const pct = Math.round((doneCount / total) * 100);
  const allDone = doneCount === total;

  // Once fully done, show a one-time celebration then let the client dismiss it.
  const dismissed = allDone && typeof window !== "undefined" && localStorage.getItem(DISMISS_KEY) === "1";
  if (dismissed) return null;

  // Circle math — using SVG so no external dep. r=42 → circumference≈263.9
  const RADIUS = 42;
  const CIRCUMFERENCE = 2 * Math.PI * RADIUS;
  const strokeDashoffset = CIRCUMFERENCE * (1 - doneCount / total);

  const nextStep = STEPS.find((s) => !flags[s.key]);

  return (
    <div
      className={
        "id-card mb-6 border " +
        (allDone ? "border-[#00CC66]/40 bg-[#00CC66]/5" : "border-[#FF3333]/40 bg-[#FF3333]/5")
      }
      data-testid="onboarding-progress-ring"
    >
      <div className="flex flex-col sm:flex-row sm:items-center gap-5">
        {/* SVG progress ring — arc rotated -90° so 0% starts at 12 o'clock.
            The count label is an HTML overlay (absolutely positioned) so it
            picks up the Oswald font naturally from the page CSS. */}
        <div className="relative flex items-center justify-center shrink-0 w-[112px] h-[112px]" data-testid="progress-ring-svg">
          <svg width="112" height="112" viewBox="0 0 100 100" className="absolute inset-0">
            <circle
              cx="50" cy="50" r={RADIUS}
              stroke="#262626" strokeWidth="8" fill="none"
            />
            <circle
              cx="50" cy="50" r={RADIUS}
              stroke={allDone ? "#00CC66" : "#FF3333"}
              strokeWidth="8"
              fill="none"
              strokeLinecap="round"
              strokeDasharray={CIRCUMFERENCE}
              strokeDashoffset={strokeDashoffset}
              transform="rotate(-90 50 50)"
              className="transition-all duration-700"
              data-testid="progress-ring-arc"
            />
          </svg>
          <span
            className="relative font-oswald text-2xl font-bold text-white tabular-nums"
            data-testid="progress-ring-count"
          >
            {doneCount}/{total}
          </span>
        </div>

        <div className="flex-1 min-w-0">
          <div className={"id-label mb-1 " + (allDone ? "text-[#00CC66]" : "text-[#FF3333]")}>
            {allDone ? "✓ Onboarding dialled" : "— Onboarding streak —"}
          </div>
          <div className="font-oswald text-2xl md:text-3xl uppercase tracking-wide leading-tight" data-testid="progress-ring-headline">
            {allDone
              ? "You're fully set up. Let's get to work."
              : `${pct}% dialled · ${total - doneCount} step${total - doneCount === 1 ? "" : "s"} to go`}
          </div>
          {!allDone && nextStep && (
            <p className="text-zinc-400 text-xs font-manrope mt-1">
              Next: <Link to={nextStep.to} className="text-white underline hover:text-[#FF3333]" data-testid="progress-ring-next-step-link">
                {nextStep.label} — {nextStep.hint}
              </Link>
            </p>
          )}
          {allDone && (
            <button
              onClick={() => { localStorage.setItem(DISMISS_KEY, "1"); window.location.reload(); }}
              className="mt-2 text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-500 hover:text-white"
              data-testid="progress-ring-dismiss-btn"
            >
              Dismiss
            </button>
          )}
        </div>
      </div>

      {/* 5-step checklist (below the ring on mobile, side-by-side on desktop) */}
      {!allDone && (
        <ul className="mt-5 pt-5 border-t border-[#262626] grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="progress-ring-checklist">
          {STEPS.map((s) => {
            const done = flags[s.key];
            const Icon = s.icon;
            return (
              <li key={s.key} data-testid={`progress-step-${s.key}`}>
                {done ? (
                  <div className="flex items-center gap-2 py-2 px-2 opacity-60" data-testid={`progress-step-${s.key}-done`}>
                    <CheckCircle size={16} weight="fill" className="text-[#00CC66] shrink-0" />
                    <span className="text-sm font-manrope text-zinc-400 line-through">{s.label}</span>
                  </div>
                ) : (
                  <Link
                    to={s.to}
                    className="flex items-center gap-2 py-2 px-2 border border-[#262626] hover:border-[#FF3333] hover:bg-[#FF3333]/5 transition-colors group"
                    data-testid={`progress-step-${s.key}-cta`}
                  >
                    <Icon size={16} weight="bold" className="text-zinc-400 group-hover:text-[#FF3333] shrink-0" />
                    <span className="text-sm font-manrope text-white flex-1 min-w-0 truncate">{s.label}</span>
                    <ArrowRight size={12} weight="bold" className="text-zinc-600 group-hover:text-[#FF3333] shrink-0" />
                  </Link>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
